import type { MetricsResponse } from "@cdt/shared";
import { BarChart3 } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";

interface EventVolumeChartProps {
  metrics: MetricsResponse;
  days: number;
  onDaysChange: (days: number) => void;
  isFetching: boolean;
}

const DAY_OPTIONS = [7, 30, 90] as const;

export function EventVolumeChart({
  metrics,
  days,
  onDaysChange,
  isFetching,
}: EventVolumeChartProps) {
  const data = metrics.items.map((item) => ({
    date: item.date,
    label: formatDate(item.date),
    total: item.total,
  }));

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100">
            <BarChart3 className="h-4 w-4" />
          </span>
          Volume eventi
        </CardTitle>
        <div className="flex items-center gap-1 rounded-full bg-slate-100/80 p-1">
          {DAY_OPTIONS.map((option) => (
            <Button
              key={option}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onDaysChange(option)}
              className={cn(
                "h-7 rounded-full px-3 text-xs font-medium",
                days === option
                  ? "bg-white text-slate-900 shadow-sm hover:bg-white"
                  : "text-slate-500 hover:bg-white/60 hover:text-slate-700",
              )}
            >
              {option}g
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className={cn("h-72 transition-opacity", isFetching && "opacity-60")}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="volumeBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#93c5fd" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                minTickGap={18}
                tick={{ fill: "#94a3b8", fontSize: 12 }}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                width={32}
                tick={{ fill: "#94a3b8", fontSize: 12 }}
              />
              <Tooltip
                cursor={{ fill: "rgba(59,130,246,0.08)" }}
                contentStyle={{
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 12px 32px -12px rgba(15,23,42,0.35)",
                  fontSize: 13,
                }}
                labelStyle={{ color: "#64748b", fontWeight: 600 }}
                formatter={(value) => [String(value ?? 0), "Eventi"]}
                labelFormatter={(label) => `Data ${label}`}
              />
              <Bar dataKey="total" fill="url(#volumeBar)" radius={[6, 6, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
